import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Paper,
  alpha,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import { formatAmount, getCommaOut } from "../../../../utils/hooks/dataHandler";
import ScheduleHeader from "../../../App/ScheduleHeader";
import { TextFieldForAmount } from "../../../utils/TextField/NumericTextField";
import {
  TextFieldForName,
  TextFieldForViewOnly,
} from "../../../utils/TextField/TextField";
import LimitedTextField from "../../../utils/TextField/LimitedTextField";
import { LIMITATION_TYPES } from "../../../../utils/static/limitation";
import PdfGenerator from "../Pdf/FaDisposalPdf";
import ScheduleNotes from "../../ScheduleNotes";
import { YearPicker } from "../../../utils/Form/YearPicker";
import {
  getCaSettingByKey,
  getCaType,
  getCaKeys,
} from "../../../../builders/settings/CaSetting";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import IconButtonWithTooltip from "../../../utils/IconButtonWithTooltip";
import { BalanceVerification } from "../../utils/ScheduleContent";
import GroupedSelect from "../../../utils/GroupedSelect";
import FullscreenDialog from "../../../utils/dialog/FullscreenDialog";
import ValidationWarningDialog from "../../../utils/dialog/ValidationWarningDialog";
import DisposalTwdvSummary from "./TwdvSummary";
import DisposalBaBcSummary from "./BaBcSummary";
import DisposalExcelUpload from "./DisposalExcelUpload";
import Toolbar from "../../../utils/Toolbar";
import { VisibilityOutlined, VisibilityOffOutlined } from "@mui/icons-material";
import { JustInfo, StyledIconButton } from "../../../utils/Icon";
import RefreshIcon from "@mui/icons-material/Refresh";
import { exportComponentAsImage } from "../../../../utils/export/componentExporter";
import loadingLogo from "../../../../theme/logo/logo_only.PNG";
import { useErrorDialog } from "../../../../contexts/ErrorDialogContext";
import { getS19DDisposalClaim } from "../CaSchedules/utils/s19dNetDisposal";

const columnSettings = [
  { type: "text", align: "left", field: "description" },
  { type: "text", align: "center", field: "CaName" },
  { type: "checkbox", align: "center", field: "isHirePurchase" },
  { type: "text", align: "center", field: "yaOfPurchase" },
  { type: "checkbox", align: "center", field: "isFullyClaimed" },
  { type: "text", align: "center", field: "noOfYearsBf" },
  { type: "numeric", align: "right", field: "costInDetails" },
  { type: "numeric", align: "right", field: "hpPrincipalPaid" },
  { type: "numeric", align: "right", field: "twdvBf" },
  { type: "numeric", align: "right", field: "salesProceeds" },
  { type: "numeric", align: "right", field: "balancingCharge" },
  { type: "numeric", align: "right", field: "netBookValue" },
  { type: "numeric", align: "right", field: "gainOrLossOnDisposal" },
  { type: "", align: "center", field: "action" },
  { type: "text", align: "center", field: "CaSummary" },
];

const NO_BA_BC_CA_NAMES = new Set(["No Claim"]);

const isNoBaBcCaName = (caName) =>
  NO_BA_BC_CA_NAMES.has(String(caName || "").trim());

const FaDisposalSchedule = ({
  company,
  schedule,
  scheduleDetails,
  saveSchedule,
  deleteSchedule,
  canEditSchedule = true,
}) => {
  const exportRef = useRef(null);
  const handleError = useErrorDialog();
  const handleExportAsImage = async (e) => {
    e.stopPropagation();
    if (exportRef.current) {
      await exportComponentAsImage(exportRef.current, "FA Disposal");
    }
  };

  const [loading, setLoading] = useState(true);
  const [tableReady, setTableReady] = useState(false);
  const [validationWarning, setValidationWarning] = useState({
    open: false,
    warnings: [],
    pendingAssetLines: null,
    pendingRegisteredAssetSources: null,
    loading: false,
  });

  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");

  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);

  const [assetLines, setAssetLines] = useState([]);
  const [registeredAssetSources, setRegisteredAssetSources] = useState({});
  const [caTypeOptions, setCaTypeOptions] = useState([]);
  const [totalAssetLines, setTotalAssetLines] = useState([]);
  const [showNetbookValue, setShowNetbookValue] = useState(false);

  const [twdvSummary, setTwdvSummary] = useState([]);
  const [bcSummary, setBcSummary] = useState([]);

  const [caSettings, setCaSettings] = useState([]);
  const [refSources, setRefSources] = useState({});
  const caTypeUploadOptions = useMemo(
    () => caTypeOptions.map((option) => option.value),
    [caTypeOptions]
  );

  const getRegisteredSourceCategoryId = (row = {}, index = 0) =>
    row.categoryId ||
    row.assetCategoryId ||
    `fa-register-category-${String(row.assetCategory || row.category || index)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")}`;

  const getRegisteredSourceCategoryAssetId = (categoryId) =>
    `fa-register-disposal-category-${categoryId}`;

  const getCategorySourceIds = (row = {}) =>
    [row.assetId, row.categoryId, row.assetCategoryId, row.assetCategory]
      .filter(Boolean)
      .map((value) => String(value).trim().toLowerCase());

  const isFaRegisterManualValuesRow = (row = {}) =>
    row.faRegisterManualValues || row.faRegisterValueMode === "manual";

  const isFaRegisterSourceDetail = (row = {}) =>
    row.faRegisterSourceDetail || row.locked;

  const editableFaRegisterSourceFields = new Set([
    "isFullyClaimed",
    "salesProceeds",
    "netBookValue",
  ]);

  const getFaRegisterDetailSourceId = (row = {}, index = 0) =>
    String(
      row.registeredAssetSourceId ||
        row.assetId ||
        row.rowKey ||
        `${row.categoryId || row.assetCategory || "fa-register"}-${index}`
    );

  const getManualFaRegisterCategoryIds = (rows = []) => {
    const ids = new Set();
    rows.forEach((row) => {
      if (!isFaRegisterManualValuesRow(row)) return;
      getCategorySourceIds(row).forEach((id) => ids.add(id));
    });
    return ids;
  };

  const isFARegisterSourceCategory = (category = {}) =>
    !isFaRegisterManualValuesRow(category) &&
    (category.source === "FA Register" ||
      category.source?.source === "FA Register" ||
      category.isImportedFromFARegister ||
      category.faRegisterSource);

  const hasDisposalDetails = (category = {}) =>
    (category.disposalDetails || []).length > 0;

  const getRegisteredCategoryIds = (sourceRows = {}) => {
    const ids = new Set();
    const addIds = (row) => {
      getCategorySourceIds(row).forEach((id) => ids.add(id));
    };

    (sourceRows.assetCategories || []).forEach(addIds);
    (sourceRows.registeredCategories || []).forEach(addIds);
    (sourceRows.categories || []).forEach(addIds);
    (sourceRows.disposalDetails || []).forEach(addIds);

    return ids;
  };

  const isMatchedFARegisterCategory = (category, registeredCategoryIds) =>
    !isFaRegisterManualValuesRow(category) &&
    (isFARegisterSourceCategory(category) ||
      getCategorySourceIds(category).some((id) =>
        registeredCategoryIds.has(id)
      ));

  const filterManualAssetLines = (
    rows = [],
    sourceRows = registeredAssetSources
  ) => {
    const registeredCategoryIds = getRegisteredCategoryIds(sourceRows);
    return rows.filter(
      (category) =>
        !isMatchedFARegisterCategory(category, registeredCategoryIds)
    );
  };

  const buildRegisteredSourceCategories = (
    sourceRows = {},
    manualRows = []
  ) => {
    const manualIds = getManualFaRegisterCategoryIds(manualRows);
    const rawDisposalDetails = Array.isArray(sourceRows.disposalDetails)
      ? sourceRows.disposalDetails
      : [];
    const sourceCategories = Array.isArray(sourceRows.categories)
      ? sourceRows.categories
      : [];
    const disposalDetails =
      rawDisposalDetails.length > 0
        ? rawDisposalDetails
        : sourceCategories.flatMap((category) =>
            (category.disposalDetails || []).map((detail) => ({
              ...detail,
              categoryId:
                detail.categoryId || category.categoryId || category.assetId,
              assetCategoryId:
                detail.assetCategoryId ||
                category.assetCategoryId ||
                category.categoryId ||
                category.assetId,
              assetCategory:
                detail.assetCategory ||
                detail.category ||
                category.assetCategory,
              category:
                detail.category ||
                detail.assetCategory ||
                category.assetCategory,
            }))
          );
    const categoryMetaById = new Map(
      sourceCategories
        .map((category) => [
          category.categoryId || category.assetCategoryId || category.assetId,
          category,
        ])
        .filter(([categoryId]) => Boolean(categoryId))
    );
    const categoryMap = new Map();

    disposalDetails.forEach((detail, index) => {
      const categoryId = getRegisteredSourceCategoryId(detail, index);
      const meta = categoryMetaById.get(categoryId) || {};
      const assetCategory =
        detail.assetCategory ||
        detail.category ||
        meta.assetCategory ||
        "Registered Assets";

      if (!categoryMap.has(categoryId)) {
        categoryMap.set(categoryId, {
          ...meta,
          source: "FA Register",
          assetId: String(meta.assetId || "").startsWith(
            "fa-register-disposal-category-"
          )
            ? meta.assetId
            : getRegisteredSourceCategoryAssetId(categoryId),
          categoryId,
          assetCategoryId: categoryId,
          assetCategory,
          isImportedFromFARegister: true,
          faRegisterSource: true,
          locked: true,
          disposalDetails: [],
        });
      }

      categoryMap.get(categoryId).disposalDetails.push({
        ...detail,
        categoryId,
        assetCategoryId: categoryId,
        assetCategory,
        category: assetCategory,
      });
    });

    return Array.from(categoryMap.values())
      .filter(
        (category) =>
          manualIds.size === 0 ||
          !getCategorySourceIds(category).some((id) => manualIds.has(id))
      )
      .filter(hasDisposalDetails)
      .map((category, index) => {
        const categoryDetails = category.disposalDetails || [];
        const disposal = categoryDetails.reduce(
          (total, detail) => total + (Number(detail.costInDetails) || 0),
          0
        );

        return {
          ...category,
          assetId: String(category.assetId || "").startsWith(
            "fa-register-disposal-category-"
          )
            ? category.assetId
            : getRegisteredSourceCategoryAssetId(
                category.categoryId ||
                  category.assetId ||
                  `fa-register-${index}`
              ),
          categoryId:
            category.categoryId || category.assetId || `fa-register-${index}`,
          assetCategory: category.assetCategory || "Registered Assets",
          cost: {
            ...(category.cost || {}),
            disposal: -disposal,
          },
        };
      });
  };

  const normalizeRegisteredAssetSources = (sourceRows = {}) => {
    const rawDisposalDetails = Array.isArray(sourceRows.disposalDetails)
      ? sourceRows.disposalDetails
      : [];
    const sourceCategories = Array.isArray(sourceRows.categories)
      ? sourceRows.categories
      : [];
    const sourceDetails =
      rawDisposalDetails.length > 0
        ? rawDisposalDetails
        : sourceCategories.flatMap((category) =>
            (category.disposalDetails || []).map((detail) => ({
              ...detail,
              categoryId:
                detail.categoryId || category.categoryId || category.assetId,
              assetCategoryId:
                detail.assetCategoryId ||
                category.assetCategoryId ||
                category.categoryId ||
                category.assetId,
              assetCategory:
                detail.assetCategory ||
                detail.category ||
                category.assetCategory,
              category:
                detail.category ||
                detail.assetCategory ||
                category.assetCategory,
            }))
          );
    const disposalDetails = sourceDetails
      ? sourceDetails.map((detail, index) => {
          const categoryId = getRegisteredSourceCategoryId(detail, index);
          const assetCategory =
            detail.assetCategory || detail.category || "Registered Assets";

          return {
            ...detail,
            categoryId,
            assetCategoryId: categoryId,
            assetCategory,
            category: assetCategory,
          };
        })
      : [];
    const disposal = disposalDetails.reduce(
      (total, detail) => total + (Number(detail.costInDetails) || 0),
      0
    );
    const normalizedSourceRows = {
      ...sourceRows,
      source: sourceRows.source || "FA Register",
      assetId: sourceRows.assetId || "registered-assets",
      assetCategory: sourceRows.assetCategory || "Registered Assets",
      disposalDetails,
      cost: {
        ...(sourceRows.cost || {}),
        disposal: -disposal,
      },
    };

    return {
      ...normalizedSourceRows,
      categories: buildRegisteredSourceCategories(normalizedSourceRows),
    };
  };

  const filterRegisteredSourceRowsByManualRows = (
    sourceRows = {},
    manualRows = []
  ) => {
    const manualIds = getManualFaRegisterCategoryIds(manualRows);
    if (manualIds.size === 0) return sourceRows;

    const isExcluded = (row = {}) =>
      getCategorySourceIds(row).some((id) => manualIds.has(id));
    const disposalDetails = (sourceRows.disposalDetails || []).filter(
      (detail) => !isExcluded(detail)
    );
    const categories = (sourceRows.categories || [])
      .filter((category) => !isExcluded(category))
      .map((category) => ({
        ...category,
        disposalDetails: (category.disposalDetails || []).filter(
          (detail) => !isExcluded(detail)
        ),
      }))
      .filter(hasDisposalDetails);
    const disposal = disposalDetails.reduce(
      (total, detail) => total + (Number(detail.costInDetails) || 0),
      0
    );

    return {
      ...sourceRows,
      disposalDetails,
      categories,
      cost: {
        ...(sourceRows.cost || {}),
        disposal: -disposal,
      },
    };
  };

  const getRegisteredSourceDetailsForManualCategory = (
    category = {},
    sourceRows = {}
  ) => {
    const categoryIds = new Set(getCategorySourceIds(category));
    const matchesCategory = (row = {}) =>
      getCategorySourceIds(row).some((id) => categoryIds.has(id));

    const detailsFromCategories = (sourceRows.categories || [])
      .filter(matchesCategory)
      .flatMap((sourceCategory) => sourceCategory.disposalDetails || []);

    if (detailsFromCategories.length > 0) return detailsFromCategories;

    return (sourceRows.disposalDetails || []).filter(matchesCategory);
  };

  const prefillManualFaRegisterDetails = (rows = [], sourceRows = {}) =>
    rows.map((category) => {
      if (!isFaRegisterManualValuesRow(category)) return category;

      const existingDetails = category.disposalDetails || [];
      const existingSourceIds = new Set(
        existingDetails.map((detail, index) =>
          getFaRegisterDetailSourceId(detail, index)
        )
      );
      const sourceDetails = getRegisteredSourceDetailsForManualCategory(
        category,
        sourceRows
      );
      const missingSourceDetails = sourceDetails
        .filter((detail, index) => {
          const sourceId = getFaRegisterDetailSourceId(detail, index);
          return !existingSourceIds.has(sourceId);
        })
        .map((detail, index) => ({
          ...detail,
          assetId: getFaRegisterDetailSourceId(detail, index),
          registeredAssetSourceId: getFaRegisterDetailSourceId(detail, index),
          categoryId: category.assetId || category.categoryId,
          assetCategoryId: category.assetId || category.categoryId,
          assetCategory: category.assetCategory,
          category: category.assetCategory,
          isImportedFromFARegister: true,
          faRegisterSource: true,
          faRegisterSourceDetail: true,
          locked: true,
        }));

      if (missingSourceDetails.length === 0) return category;

      return {
        ...category,
        disposalDetails: [...missingSourceDetails, ...existingDetails],
      };
    });

  const registeredSourceCategories = buildRegisteredSourceCategories(
    registeredAssetSources,
    assetLines
  );

  const hasRegisteredAssetSources = registeredSourceCategories.length > 0;
  const visibleAssetLines = useMemo(() => {
    const registeredCategoryIds = getRegisteredCategoryIds(
      registeredAssetSources
    );
    return assetLines.reduce((list, category, originalIndex) => {
      if (!isMatchedFARegisterCategory(category, registeredCategoryIds)) {
        list.push({ category, originalIndex });
      }
      return list;
    }, []);
  }, [assetLines, registeredAssetSources]);

  const getCombinedAssetLines = (
    nextAssetLines = assetLines,
    nextRegisteredAssetSources = registeredAssetSources
  ) => {
    // Reuse the categories already prepared for the current table.
    if (
      nextAssetLines === assetLines &&
      nextRegisteredAssetSources === registeredAssetSources
    ) {
      return [
        ...visibleAssetLines.map(({ category }) => category),
        ...registeredSourceCategories,
      ];
    }

    const nextRegisteredSourceCategories = buildRegisteredSourceCategories(
      nextRegisteredAssetSources,
      nextAssetLines
    );
    const nextManualAssetLines = filterManualAssetLines(
      nextAssetLines,
      nextRegisteredAssetSources
    );

    return nextRegisteredSourceCategories.length > 0
      ? [...nextManualAssetLines, ...nextRegisteredSourceCategories]
      : nextManualAssetLines;
  };

  const handleOpenExcelUploadDialog = () => setExcelDialogOpen(true);
  const handleCloseExcelUploadDialog = () => setExcelDialogOpen(false);

  const buildAssetByAssetDisposalDetail = (asset) => ({
    assetId: asset.assetId,
    description: asset.assetCategory || "",
    CaName: "",
    CaSummary: "",
    isHirePurchase: false,
    yaOfPurchase: "",
    isFullyClaimed: false,
    noOfYearsBf: 0,
    costInDetails: Math.abs(asset.cost?.disposal || 0),
    hpPrincipalPaid: 0,
    twdvBf: 0,
    salesProceeds: 0,
    netBookValue: 0,
    bcBa: 0,
    disposalGainLoss: 0,
  });

  const normalizeAssetByAssetDisposalRows = (rows = []) =>
    rows.map((asset) => {
      if (!asset.isAssetByAsset || !asset.isAssetByAssetDisposed) {
        return asset;
      }

      if (asset.disposalDetails?.length > 0) {
        return asset;
      }

      return {
        ...asset,
        disposalDetails: [buildAssetByAssetDisposalDetail(asset)],
      };
    });

  const calculateManualData = (dataRows, rowCaSettings) => {
    const appliedClaims = new Set();
    return dataRows.map((asset) => {
      return {
        ...asset,
        disposalDetails: asset.disposalDetails.map((detail) => {
          let calculatedBcBa = detail.isHirePurchase
            ? Math.max(
                detail.twdvBf - detail.salesProceeds,
                -(detail.hpPrincipalPaid - detail.twdvBf)
              )
            : Math.max(
                detail.twdvBf - detail.salesProceeds,
                -(detail.costInDetails - detail.twdvBf)
              );

          const s19dClaim =
            getCaSettingByKey(rowCaSettings, detail.CaName)?.caType === "S19D"
              ? getS19DDisposalClaim(detail, scheduleDetails?.s19dClaims || [])
              : null;
          if (s19dClaim) {
            calculatedBcBa = appliedClaims.has(s19dClaim.key)
              ? 0
              : s19dClaim.bcBa || 0;
            appliedClaims.add(s19dClaim.key);
          }

          if (!detail.CaSummary) {
            detail.CaSummary =
              detail.CaName === "S16 - (LIA / IBA)"
                ? ""
                : getCaSettingByKey(rowCaSettings, detail.CaName)?.caSummary;
          }

          return {
            ...detail,
            bcBa: calculatedBcBa,
            s19dDisposalWarning: s19dClaim?.warning || "",
            disposalGainLoss: detail.salesProceeds - detail.netBookValue,
          };
        }),
      };
    });
  };

  function getUnallocatedPrincipal(asset, yearOfAssessment) {
    // 1. Sum all principalPaid amounts up to YA
    const totalPaid = asset.principalPaid
      .filter((p) => p.year <= yearOfAssessment)
      .reduce((sum, p) => sum + p.amount, 0);

    // 2. Sum all amounts in principalDetails for years <= YA
    let totalAllocated = 0;
    if (asset.caType !== "S19") {
      for (const [year, allocations] of Object.entries(
        asset.principalDetails
      )) {
        if (parseInt(year) <= yearOfAssessment) {
          totalAllocated += allocations.reduce((sum, a) => sum + a.amount, 0);
        }
      }
    } else {
      const allPrincipalYears = Object.keys(asset.principalDetails || {})
        .map(Number)
        .sort((a, b) => a - b);

      const principalPaidMap = {};
      for (const { year, amount } of asset.principalPaid || []) {
        principalPaidMap[year] = amount;
      }

      const yearTotals = {};
      const twdvTotals = {};
      const finalTotals = {};

      let finalAmount = 0;

      let lastTotal = 0;
      allPrincipalYears.forEach((year) => {
        const entries = asset.principalDetails?.[year] || [];

        function calculateYearTotal(entries) {
          return entries.reduce((sum, entry) => {
            if (entry.deferred || entry.currentYearDisposed || entry.disposed) {
              return sum;
            }
            // Sum AA + IA (if any) for the entry
            const aa = entry.aa || 0;
            const ia = entry.ia || 0;
            return sum + aa + ia;
          }, 0);
        }

        let total = calculateYearTotal(entries);
        yearTotals[year] = total;

        const cur_pp = principalPaidMap[year] || 0;

        let netTotal = lastTotal + cur_pp - total;
        twdvTotals[year] = lastTotal;
        finalTotals[year] = netTotal;

        lastTotal = netTotal;

        if (year >= yearOfAssessment) {
          finalAmount = finalTotals[yearOfAssessment];
          return;
        }
      });

      return finalAmount;
    }

    return totalPaid - totalAllocated;
  }

  const generateHpDisposalRows = (
    curDisposalRows,
    disposedHpAssets,
    hpSummary,
    yearOfAssessment,
    caSettings
  ) => {
    if (
      !hpSummary.hasDisposedAssets ||
      !disposedHpAssets ||
      disposedHpAssets.length < 1
    )
      return curDisposalRows;

    disposedHpAssets.forEach((asset) => {
      const index = curDisposalRows.findIndex(
        (item) => item.assetId === asset.categoryId
      );

      if (index === -1) return;

      const curDisposal = curDisposalRows[index];

      const buildRow = (extra = {}) => {
        const curPp = asset.principalPaid
          .filter((item) => item.year <= yearOfAssessment)
          .reduce((sum, item) => sum + item.amount, 0);

        let yaOfPurchase = asset.principalPaid[0]?.year || yearOfAssessment;
        // If extra provided and contains yaOfPurchase, override the yaOfPurchase to ensure latest data
        if (extra && typeof extra.yaOfPurchase !== "undefined") {
          extra.yaOfPurchase = yaOfPurchase;
        }

        return {
          asset: asset,
          assetId: asset.assetId,
          description: asset.assetName,
          CaName: asset.caType,
          CaSummary: getCaSettingByKey(caSettings, asset.caType)?.caSummary,
          isHirePurchase: true,
          yaOfPurchase: yaOfPurchase,
          isFullyClaimed: false,
          noOfYearsBf: asset.noOfYears,
          costInDetails: asset.cost,
          hpPrincipalPaid: curPp,
          twdvBf: getUnallocatedPrincipal(asset, yearOfAssessment),
          salesProceeds: 0,
          netBookValue: 0,
          bcBa: 0,
          disposalGainLoss: 0,
          ...extra, // preserve or override values
        };
      };

      const existingIndex = (curDisposal.disposalDetails || []).findIndex(
        (d) => d.assetId === asset.assetId
      );

      let updatedDetails = [...(curDisposal.disposalDetails || [])];

      if (existingIndex !== -1) {
        const existingRow = updatedDetails[existingIndex];
        updatedDetails[existingIndex] = buildRow({
          ...existingRow,
          asset: asset,
          twdvBf:
            existingRow.twdvBf ??
            getUnallocatedPrincipal(asset, yearOfAssessment),
        });
      } else {
        // Add new row with calculated twdvBf
        updatedDetails.push(buildRow());
      }

      curDisposalRows[index] = {
        ...curDisposal,
        disposalDetails: updatedDetails,
      };
    });

    // Build a lookup of disposed assetIds
    const disposedAssetIds = new Set(disposedHpAssets.map((a) => a.assetId));
    curDisposalRows = curDisposalRows.map((row) => ({
      ...row,
      disposalDetails: (row.disposalDetails || []).filter((detail) => {
        // keep non-HP rows
        if (!detail.isHirePurchase) return true;

        // keep HP rows only if assetId still exists
        return disposedAssetIds.has(detail.assetId);
      }),
    }));

    return curDisposalRows;
  };

  const calculateTotal = (dataRows) => {
    let disposalTotalDetails = {
      dataRows: [],
      overallTotalDetails: {},
    };

    let overallTotals = {};
    let groupedTWDV = {};
    let netBcBa = 0;

    dataRows.forEach((row) => {
      let totalDetails = {};

      row.disposalDetails.forEach((detail) => {
        if (!isCaNameDisabled(detail)) {
          const assetKey = row.isAssetByAsset ? ` | ${row.assetId}` : "";
          const twdvKey = `${detail.CaName} | ${detail.yaOfPurchase} | ${detail.isHirePurchase} | ${detail.noOfYearsBf}${assetKey}`;
          const detailCaType = getCaSettingByKey(
            caSettings,
            detail.CaName
          )?.caType;

          if (!groupedTWDV[twdvKey]) {
            groupedTWDV[twdvKey] = {
              caName: detail.CaName,
              caType: detailCaType,
              yaOfPurchase: detail.yaOfPurchase,
              noOfYearsBf: detail.noOfYearsBf,
              isHirePurchase: detail.isHirePurchase,
              ...(detailCaType === "S19D" ? { cost: 0, salesProceeds: 0 } : {}),
              twdv: 0,
            };
          }
          groupedTWDV[twdvKey].twdv += detail.twdvBf;
          if (detailCaType === "S19D") {
            groupedTWDV[twdvKey].cost += Number(detail.costInDetails) || 0;
            groupedTWDV[twdvKey].salesProceeds +=
              Number(detail.salesProceeds) || 0;
          }

          if (!isNoBaBcCaName(detail.CaName))
            netBcBa += Number(detail.bcBa) || 0;
        }

        Object.keys(detail).forEach((key) => {
          const excludeFromTotal =
            key === "bcBa" && isNoBaBcCaName(detail.CaName);

          if (
            typeof detail[key] === "number" &&
            key !== "noOfYearsBf" &&
            !excludeFromTotal
          ) {
            totalDetails[key] = (totalDetails[key] || 0) + detail[key];
            overallTotals[key] = (overallTotals[key] || 0) + detail[key];
          }
        });
      });

      let costCheck =
        totalDetails.costInDetails === Math.abs(row.cost.disposal);

      disposalTotalDetails.dataRows.push({
        assetId: row.assetId,
        totalDetails,
        costCheck,
      });
    });

    disposalTotalDetails.overallTotalDetails = overallTotals;

    const nextTwdvSummary = Object.values(groupedTWDV);
    const nextBcSummary =
      netBcBa === 0
        ? []
        : [
            {
              caName:
                netBcBa > 0
                  ? "Net Balancing Allowance"
                  : "Net Balancing Charge",
              bcBa: netBcBa,
            },
          ];

    setTotalAssetLines(disposalTotalDetails);
    setTwdvSummary(nextTwdvSummary);
    setBcSummary(nextBcSummary);

    return {
      totalAssetLines: disposalTotalDetails,
      twdvSummary: nextTwdvSummary,
      bcSummary: nextBcSummary,
    };
  };

  const buildScheduleDetails = (
    nextAssetLines = assetLines,
    nextRegisteredAssetSources = registeredAssetSources,
    totals = {
      totalAssetLines,
      twdvSummary,
      bcSummary,
    }
  ) => {
    const normalizedRegisteredAssetSources = normalizeRegisteredAssetSources(
      nextRegisteredAssetSources
    );

    return {
      dataRows: nextAssetLines,
      registeredAssetSourceRows: normalizedRegisteredAssetSources,
      showNetbookValue: showNetbookValue,
      TWDVSummary: totals.twdvSummary,
      BCBASummary: totals.bcSummary,
      doc: JSON.stringify({
        assetLines: getCombinedAssetLines(
          nextAssetLines,
          normalizedRegisteredAssetSources
        ),
        registeredAssetSources: normalizedRegisteredAssetSources,
        totalAssetLines: totals.totalAssetLines,
        showNetbookValue,
        twdvSummary: totals.twdvSummary,
        bcSummary: totals.bcSummary,
        refSources,
        notes,
        reviewNotes,
      }),
    };
  };

  const saveRows = (nextAssetLines, nextRegisteredAssetSources, totals) => {
    const invalid = getCombinedAssetLines(
      nextAssetLines,
      nextRegisteredAssetSources
    )
      .flatMap((asset) => asset.disposalDetails || [])
      .find((detail) => detail.s19dDisposalWarning);
    if (invalid) {
      handleError(invalid.s19dDisposalWarning);
      return;
    }
    schedule.notes = notes;
    schedule.reviewNotes = reviewNotes;
    saveSchedule(
      schedule,
      buildScheduleDetails(nextAssetLines, nextRegisteredAssetSources, totals)
    );
  };

  const getValidationWarnings = (
    nextAssetLines = assetLines,
    nextRegisteredAssetSources = registeredAssetSources
  ) => {
    const warnings = [];

    getCombinedAssetLines(nextAssetLines, nextRegisteredAssetSources).forEach(
      (asset) => {
        const currentTotal = (asset.disposalDetails || []).reduce(
          (total, detail) => total + (Number(detail.costInDetails) || 0),
          0
        );
        const expectedTotal = Math.abs(Number(asset.cost?.disposal) || 0);

        if (currentTotal !== expectedTotal) {
          warnings.push({
            title: `${asset.assetCategory || "Asset category"} disposal cost does not match`,
            description: `Current: ${formatAmount(
              currentTotal
            )}. Expected: ${formatAmount(expectedTotal)}. Difference: ${formatAmount(
              expectedTotal - currentTotal
            )}.`,
          });
        }
      }
    );

    return warnings;
  };

  const performSave = (
    nextAssetLines = assetLines,
    nextRegisteredAssetSources = registeredAssetSources
  ) => {
    const totals = calculateTotal(
      getCombinedAssetLines(nextAssetLines, nextRegisteredAssetSources)
    );
    saveRows(nextAssetLines, nextRegisteredAssetSources, totals);
  };

  const handleSave = (options = {}) => {
    const warnings = options.skipValidationWarning
      ? []
      : getValidationWarnings(assetLines, registeredAssetSources);

    if (warnings.length > 0) {
      setValidationWarning({
        open: true,
        warnings,
        pendingAssetLines: assetLines,
        pendingRegisteredAssetSources: registeredAssetSources,
        loading: false,
      });
      return false;
    }

    performSave(assetLines, registeredAssetSources);
    return true;
  };

  const handleConfirmValidationSave = () => {
    const pendingAssetLines = validationWarning.pendingAssetLines;
    const pendingRegisteredAssetSources =
      validationWarning.pendingRegisteredAssetSources;
    if (!pendingAssetLines) return;

    try {
      setValidationWarning((prev) => ({ ...prev, loading: true }));
      performSave(pendingAssetLines, pendingRegisteredAssetSources);
      setValidationWarning({
        open: false,
        warnings: [],
        pendingAssetLines: null,
        pendingRegisteredAssetSources: null,
        loading: false,
      });
    } catch (error) {
      setValidationWarning((prev) => ({ ...prev, loading: false }));
      handleError(error);
    }
  };

  const onExcelAddRows = (updatedAssetLines) => {
    const nextManualAssetLines = filterManualAssetLines(
      updatedAssetLines,
      registeredAssetSources
    );
    const calculatedRows = calculateManualData(
      nextManualAssetLines,
      caSettings
    );
    const totals = calculateTotal(
      getCombinedAssetLines(calculatedRows, registeredAssetSources)
    );

    setAssetLines(calculatedRows);
    saveRows(calculatedRows, registeredAssetSources, totals);
    handleCloseExcelUploadDialog();
  };

  const handleInputChange = (value, { index, c_index, field, field_type }) => {
    let updatedRows = [...assetLines];
    const currentRow = updatedRows[c_index]?.disposalDetails?.[index];

    if (
      isFaRegisterSourceDetail(currentRow) &&
      !editableFaRegisterSourceFields.has(field)
    ) {
      return;
    }

    if ((field === "isFullyClaimed" && value === true) || field === "CaName") {
      updatedRows[c_index].disposalDetails[index]["noOfYearsBf"] = 0;
    }

    if (field === "isHirePurchase") {
      updatedRows[c_index].disposalDetails[index]["hpPrincipalPaid"] = 0;
    }

    if (field_type === "numeric") {
      value = Number(getCommaOut(value));
    }

    if (["balancingCharge"].includes(field)) {
      if (updatedRows[c_index].disposalDetails[index]["isHirePurchase"]) {
        updatedRows[c_index].disposalDetails[index][field] = Math.min(
          updatedRows[c_index].disposalDetails[index]["salesProceeds"] -
            updatedRows[c_index].disposalDetails[index]["twdvBf"],
          updatedRows[c_index].disposalDetails[index]["hpPrincipalPaid"]
        );
      } else {
        updatedRows[c_index].disposalDetails[index][field] = Math.min(
          updatedRows[c_index].disposalDetails[index]["salesProceeds"] -
            updatedRows[c_index].disposalDetails[index]["twdvBf"],
          updatedRows[c_index].disposalDetails[index]["costInDetails"]
        );
      }
    } else updatedRows[c_index].disposalDetails[index][field] = value;

    if (field === "CaName") {
      if (!["Revenue", "No Claim", "Renovation"].includes(value)) {
        updatedRows[c_index].disposalDetails[index]["CaSummary"] =
          value === "S16 - (LIA / IBA)"
            ? ""
            : getCaSettingByKey(caSettings, value)?.caSummary || "";
      } else {
        updatedRows[c_index].disposalDetails[index]["CaSummary"] = value;
      }
    }

    if (field === "isFullyClaimed" && isFaRegisterSourceDetail(currentRow)) {
      updatedRows[c_index].disposalDetails[index]["noOfYearsBf"] = value
        ? 0
        : (updatedRows[c_index].disposalDetails[index]["sourceNoOfYearsBf"] ??
          updatedRows[c_index].disposalDetails[index]["noOfYearsBf"]);
    }

    updatedRows = calculateManualData(updatedRows, caSettings);
    calculateTotal(getCombinedAssetLines(updatedRows, registeredAssetSources));

    setAssetLines(updatedRows);
  };

  const handleRegisteredAssetSourceInputChange = (
    value,
    { index, field, field_type }
  ) => {
    const updatedRegisteredAssetSources = {
      ...registeredAssetSources,
      disposalDetails: [...(registeredAssetSources.disposalDetails || [])],
    };

    if (index < 0 || !updatedRegisteredAssetSources.disposalDetails[index]) {
      return;
    }

    if (field_type === "numeric") {
      value = Number(getCommaOut(value));
    }

    const nextDetail = {
      ...updatedRegisteredAssetSources.disposalDetails[index],
      [field]: value,
    };

    if (field === "isFullyClaimed") {
      nextDetail.noOfYearsBf = value
        ? 0
        : (nextDetail.sourceNoOfYearsBf ?? nextDetail.noOfYearsBf);
    }

    updatedRegisteredAssetSources.disposalDetails[index] = {
      ...nextDetail,
    };

    const calculatedRegisteredAssetSources = calculateManualData(
      [updatedRegisteredAssetSources],
      caSettings
    )[0];
    const normalizedRegisteredAssetSources = normalizeRegisteredAssetSources(
      calculatedRegisteredAssetSources
    );

    calculateTotal(
      getCombinedAssetLines(assetLines, normalizedRegisteredAssetSources)
    );

    setRegisteredAssetSources(normalizedRegisteredAssetSources);
  };

  useEffect(() => {
    // Let the loading indicator paint before preparing the rows.
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        //init
        if (schedule) {
          setNotes(schedule.notes);
          setReviewNotes(schedule.reviewNotes);

          setCaSettings(schedule.caSettings);
          setRefSources(schedule.refSources || schedule.scheduleMapping || {});
        }

        if (scheduleDetails) {
          const hasManualRows =
            scheduleDetails.dataRows && scheduleDetails.dataRows.length > 0;
          const hasRegisteredRows =
            scheduleDetails.registeredAssetSources?.disposalDetails?.length >
              0 ||
            scheduleDetails.registeredAssetSources?.categories?.some(
              (category) => category.disposalDetails?.length > 0
            );

          if (hasManualRows || hasRegisteredRows) {
            const nextDataRows = generateHpDisposalRows(
              scheduleDetails.dataRows || [],
              scheduleDetails.disposedHpAssets,
              scheduleDetails.hirePurchaseSummary,
              company.yearOfAssessment,
              schedule.caSettings
            );

            let data = calculateManualData(nextDataRows, schedule.caSettings);
            const registeredData = scheduleDetails.registeredAssetSources
              ?.disposalDetails?.length
              ? normalizeRegisteredAssetSources(
                  calculateManualData(
                    [scheduleDetails.registeredAssetSources],
                    schedule.caSettings
                  )[0]
                )
              : normalizeRegisteredAssetSources(
                  scheduleDetails.registeredAssetSources || {}
                );
            data = filterManualAssetLines(data, registeredData);
            data = prefillManualFaRegisterDetails(data, registeredData);
            const visibleRegisteredData =
              filterRegisteredSourceRowsByManualRows(registeredData, data);

            setAssetLines(data);
            setRegisteredAssetSources(visibleRegisteredData);
            setShowNetbookValue(
              scheduleDetails.disposalSchSettings.showNetbookValue
            );

            // Calculate totals
            calculateTotal(getCombinedAssetLines(data, visibleRegisteredData));
          }
        }

        const options = [
          ...getCaKeys(schedule.caSettings),
          "Renovation",
          "Revenue",
          "No Claim",
        ];
        setCaTypeOptions(
          options.map((opt) => {
            const val = getCaType(schedule.caSettings, opt);
            return {
              value: opt,
              label: opt,
              group: val,
            };
          })
        );
        setTableReady(true);
      });
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (!tableReady) return;

    // Keep the overlay through the populated table's commit and next paint opportunity.
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => setLoading(false));
    });
    return () => cancelAnimationFrame(frame);
  }, [tableReady]);

  const addNewRow = (categoryIndex) => {
    const newRow = {
      description: "",
      CaName: "",
      CaSummary: "",
      isHirePurchase: false,
      yaOfPurchase: "",
      isFullyClaimed: false,
      noOfYearsBf: 0,
      costInDetails: 0,
      hpPrincipalPaid: 0,
      twdvBf: 0,
      salesProceeds: 0,
      netBookValue: 0,
      bcBa: 0,
      disposalGainLoss: 0,
    };
    const updatedData = [...assetLines];
    updatedData[categoryIndex].disposalDetails.push(newRow);
    setAssetLines(updatedData);
  };

  const removeRow = (categoryIndex, rowIndex) => {
    if (
      isFaRegisterSourceDetail(
        assetLines[categoryIndex]?.disposalDetails?.[rowIndex]
      )
    ) {
      return;
    }

    let updatedRows = assetLines.map((row, index) => {
      if (index === categoryIndex) {
        return {
          ...row,
          disposalDetails: row.disposalDetails.filter((_, i) => i !== rowIndex),
        };
      }
      return row;
    });
    setAssetLines(updatedRows);
    calculateTotal(getCombinedAssetLines(updatedRows, registeredAssetSources));
  };

  // Helper function to check if CA Name related fields should be disabled
  const isCaNameDisabled = (row) => {
    return !row.CaName || row.CaName === "";
  };

  const netBookValueSwitch = (
    <Button
      variant={showNetbookValue ? "contained" : "outlined"}
      size='small'
      startIcon={
        showNetbookValue ? <VisibilityOffOutlined /> : <VisibilityOutlined />
      }
      onClick={() => setShowNetbookValue(!showNetbookValue)}
    >
      {showNetbookValue ? "Hide" : "Show"} Net Book Value
    </Button>
  );

  const registeredAssetSourceIndexById = useMemo(() => {
    const indexById = new Map();
    (registeredAssetSources.disposalDetails || []).forEach((detail, index) => {
      const id = detail.registeredAssetSourceId || detail.assetId;
      // Preserve findIndex's first-match behavior for duplicate source IDs.
      if (!indexById.has(id)) indexById.set(id, index);
    });
    return indexById;
  }, [registeredAssetSources]);

  const totalAssetById = useMemo(() => {
    const assetById = new Map();
    (totalAssetLines.dataRows || []).forEach((asset) => {
      if (!assetById.has(asset.assetId)) assetById.set(asset.assetId, asset);
    });
    return assetById;
  }, [totalAssetLines]);

  const renderRegisteredSourceCategory = (category, c_index) => {
    const categoryDetails = category.disposalDetails || [];
    const targetAsset = totalAssetById.get(category.assetId);

    return (
      <React.Fragment key={category.categoryId || category.assetId || c_index}>
        <TableRow>
          <TableCell
            style={{
              position: "sticky",
              left: 0,
              background: "white",
              zIndex: 2,
              boxShadow: "2px 0 5px rgba(0, 0, 0, 0.05)",
              fontWeight: "bold",
            }}
          >
            {category.assetCategory || "Registered Assets"}{" "}
            <JustInfo
              title='Imported from FA Register. If you want to make changes to the basic information of the asset, please update it in the FA Register.'
              ml={0.75}
              mr={0}
            />
          </TableCell>
          <TableCell colSpan={showNetbookValue ? 13 : 11}></TableCell>
          {canEditSchedule && (
            <TableCell
              align='center'
              style={{
                position: "sticky",
                right: 0,
                background: "white",
                boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
                minWidth: 100,
              }}
            ></TableCell>
          )}
        </TableRow>

        {categoryDetails.map((row, i) => {
          const sourceIndex =
            registeredAssetSourceIndexById.get(
              row.registeredAssetSourceId || row.assetId
            ) ?? -1;
          const disableInput = sourceIndex < 0 || isCaNameDisabled(row);

          return (
            <TableRow key={row.registeredAssetSourceId || row.assetId || i}>
              <TableCell
                style={{
                  position: "sticky",
                  left: 0,
                  background: "white",
                  zIndex: 2,
                  boxShadow: "2px 0 5px rgba(0, 0, 0, 0.05)",
                }}
                align={columnSettings[0].align}
              >
                <TextFieldForViewOnly value={row.description} />
              </TableCell>
              <TableCell align={columnSettings[1].align}>
                <TextFieldForViewOnly value={row.CaName || ""} />
              </TableCell>
              <TableCell align={columnSettings[14].align}>
                <TextFieldForViewOnly
                  value={
                    ["S16 - LIA", "S16 - IBA"].includes(row.CaName)
                      ? row.CaName
                      : row.CaSummary
                  }
                />
              </TableCell>
              <TableCell align={columnSettings[2].align}>
                <TextFieldForViewOnly value='No' />
              </TableCell>
              <TableCell align={columnSettings[3].align}>
                <TextFieldForViewOnly value={row.yaOfPurchase} />
              </TableCell>
              <TableCell align={columnSettings[4].align}>
                {canEditSchedule ? (
                  <Checkbox
                    checked={row.isFullyClaimed}
                    onChange={(e) =>
                      handleRegisteredAssetSourceInputChange(e.target.checked, {
                        index: sourceIndex,
                        field: columnSettings[4].field,
                        field_type: columnSettings[4].type,
                      })
                    }
                    disabled={disableInput}
                  />
                ) : (
                  <TextFieldForViewOnly
                    value={row.isFullyClaimed ? "Yes" : "No"}
                  />
                )}
              </TableCell>
              <TableCell align={columnSettings[5].align}>
                <TextFieldForViewOnly
                  value={row.isFullyClaimed ? "Fully Claimed" : row.noOfYearsBf}
                />
              </TableCell>
              <TableCell align={columnSettings[6].align}>
                <TextFieldForViewOnly value={formatAmount(row.costInDetails)} />
              </TableCell>
              <TableCell align={columnSettings[7].align}>
                <TextFieldForViewOnly value='-' />
              </TableCell>
              <TableCell align={columnSettings[8].align}>
                <TextFieldForViewOnly value={formatAmount(row.twdvBf)} />
              </TableCell>
              <TableCell align={columnSettings[9].align}>
                <TextFieldForAmount
                  value={row.salesProceeds}
                  onChange={handleRegisteredAssetSourceInputChange}
                  reference={{
                    index: sourceIndex,
                    field: columnSettings[9].field,
                    field_type: columnSettings[9].type,
                  }}
                  disabled={disableInput}
                  canEdit={canEditSchedule}
                />
              </TableCell>
              <TableCell align={columnSettings[10].align}>
                <TextFieldForViewOnly
                  value={
                    isNoBaBcCaName(row.CaName) ? "N.A." : formatAmount(row.bcBa)
                  }
                />
              </TableCell>
              {showNetbookValue && (
                <TableCell align={columnSettings[11].align}>
                  <TextFieldForAmount
                    value={row.netBookValue}
                    onChange={handleRegisteredAssetSourceInputChange}
                    reference={{
                      index: sourceIndex,
                      field: columnSettings[11].field,
                      field_type: columnSettings[11].type,
                    }}
                    disabled={disableInput}
                    canEdit={canEditSchedule}
                  />
                </TableCell>
              )}
              {showNetbookValue && (
                <TableCell align={columnSettings[12].align}>
                  <TextFieldForViewOnly
                    value={formatAmount(row.disposalGainLoss)}
                  />
                </TableCell>
              )}
              {canEditSchedule && (
                <TableCell
                  style={{
                    position: "sticky",
                    right: 0,
                    background: "white",
                    boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
                    minWidth: 100,
                  }}
                ></TableCell>
              )}
            </TableRow>
          );
        })}

        {targetAsset && (
          <TableRow key={targetAsset.assetId}>
            <TableCell
              style={{
                position: "sticky",
                left: 0,
                background: "white",
                zIndex: 2,
                boxShadow: "2px 0 5px rgba(0, 0, 0, 0.05)",
                fontWeight: "bold",
                textAlign: "right",
              }}
            >
              Sub Total
            </TableCell>
            <TableCell colSpan={6}></TableCell>
            <TableCell align={columnSettings[6].align}>
              <BalanceVerification
                grossBalance={targetAsset.totalDetails.costInDetails || 0}
                expectedTotal={Math.abs(Number(category.cost?.disposal) || 0)}
              />
            </TableCell>
            <TableCell align={columnSettings[7].align}>
              <TextFieldForViewOnly
                value={formatAmount(targetAsset.totalDetails.hpPrincipalPaid)}
              />
            </TableCell>
            <TableCell align={columnSettings[8].align}>
              <TextFieldForViewOnly
                value={formatAmount(targetAsset.totalDetails.twdvBf)}
              />
            </TableCell>
            <TableCell align={columnSettings[9].align}>
              <TextFieldForViewOnly
                value={formatAmount(targetAsset.totalDetails.salesProceeds)}
              />
            </TableCell>
            <TableCell align={columnSettings[10].align}>
              <TextFieldForViewOnly
                value={formatAmount(targetAsset.totalDetails.bcBa)}
              />
            </TableCell>
            {showNetbookValue && (
              <TableCell align={columnSettings[11].align}>
                <TextFieldForViewOnly
                  value={formatAmount(targetAsset.totalDetails.netBookValue)}
                />
              </TableCell>
            )}
            {showNetbookValue && (
              <TableCell align={columnSettings[12].align}>
                <TextFieldForViewOnly
                  value={formatAmount(
                    targetAsset.totalDetails.disposalGainLoss
                  )}
                />
              </TableCell>
            )}
            {canEditSchedule && (
              <TableCell
                style={{
                  position: "sticky",
                  right: 0,
                  background: "white",
                  boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
                  minWidth: 100,
                }}
              ></TableCell>
            )}
          </TableRow>
        )}
      </React.Fragment>
    );
  };

  const renderMainContent = () => (
    <div style={{ width: "100%" }} ref={exportRef}>
      <Toolbar
        title={`Schedule ${schedule.scheduleNo}`}
        subtitle={schedule.type}
        onToggleFullscreen={() => setFullscreenOpen(!fullscreenOpen)}
        fullscreenOpen={fullscreenOpen}
        allowSticky={true}
        size='medium'
        additionalControls={netBookValueSwitch}
        handleExportAsImage={handleExportAsImage}
        exportRef={exportRef}
        canEdit={canEditSchedule}
      />

      <Paper
        sx={{
          display: "flex",
          flexDirection: "row",
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.98),
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          height: fullscreenOpen ? 790 : "750px",
        }}
      >
        {tableReady && (
          <TableContainer
            component={Paper}
            elevation={0}
            style={{
              display: "block",
              maxWidth: "100%",
              maxHeight: "100%",
              overflow: "auto",
              minHeight: fullscreenOpen ? 790 : "750px",
            }}
          >
            <Table
              stickyHeader
              size='small'
              sx={{
                "& .MuiTableCell-root": {
                  px: 1.5,
                  py: 1.25,
                  fontSize: "0.8125rem",
                },
                "& .MuiTableCell-head": {
                  py: 1.5,
                  fontWeight: 600,
                  lineHeight: 1.4,
                  bgcolor: "background.paper",
                  borderBottom: "2px solid",
                  borderBottomColor: "divider",
                },
                "& .MuiTableCell-body .MuiInputBase-root": {
                  fontSize: "inherit",
                },
                "& .MuiTableCell-body .MuiSelect-select": {
                  fontSize: "inherit",
                },
                "& .MuiTableCell-body.MuiTableCell-alignRight": {
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    style={{
                      position: "sticky",
                      top: 0,
                      left: 0,
                      background: "white",
                      zIndex: 1000,
                      boxShadow: "2px 0 5px rgba(0, 0, 0, 0.05)",
                      minWidth: 300,
                      maxWidth: 300,
                    }}
                  >
                    Description of Asset
                  </TableCell>
                  <TableCell
                    style={{ minWidth: 200, maxWidth: 200 }}
                    align='center'
                  >
                    CA Name
                  </TableCell>
                  <TableCell
                    style={{ minWidth: 200, maxWidth: 200 }}
                    align='center'
                  >
                    CA Summary
                  </TableCell>
                  <TableCell
                    style={{ minWidth: 120, maxWidth: 120 }}
                    align='center'
                  >
                    Hire Purchase?
                  </TableCell>
                  <TableCell
                    style={{ minWidth: 100, maxWidth: 100 }}
                    align='center'
                  >
                    YA of Purchase
                  </TableCell>
                  <TableCell
                    style={{ minWidth: 120, maxWidth: 120 }}
                    align='center'
                  >
                    Fully Claimed?
                  </TableCell>
                  <TableCell
                    style={{ minWidth: 120, maxWidth: 120 }}
                    align='center'
                  >
                    No. of Years b/f
                  </TableCell>
                  <TableCell
                    style={{ minWidth: 150, maxWidth: 150 }}
                    align='right'
                  >
                    Cost
                  </TableCell>
                  <TableCell
                    style={{ minWidth: 150, maxWidth: 150 }}
                    align='right'
                  >
                    HP <br />
                    Principal Paid
                  </TableCell>
                  <TableCell
                    style={{ minWidth: 160, maxWidth: 160 }}
                    align='right'
                  >
                    TWDV B/F <br /> (a)
                  </TableCell>
                  <TableCell
                    style={{ minWidth: 150, maxWidth: 150 }}
                    align='right'
                  >
                    Sales Proceeds <br /> (b)
                  </TableCell>
                  <TableCell
                    style={{ minWidth: 180, maxWidth: 180 }}
                    align='right'
                  >
                    Balancing Allowance / (Charge) <br />
                    (c) = (a) - (b)
                  </TableCell>
                  {showNetbookValue && (
                    <TableCell
                      style={{ minWidth: 150, maxWidth: 150 }}
                      align='right'
                    >
                      Net Book Value <br /> (d)
                    </TableCell>
                  )}
                  {showNetbookValue && (
                    <TableCell
                      style={{ minWidth: 180, maxWidth: 180 }}
                      align='right'
                    >
                      Gain or (loss) on disposal <br /> (e) = (b) - (d)
                    </TableCell>
                  )}
                  {canEditSchedule && (
                    <TableCell
                      style={{
                        position: "sticky",
                        top: 0,
                        right: 0,
                        background: "white",
                        zIndex: 2,
                        boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
                      }}
                      align='center'
                    >
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleAssetLines.map(({ category, originalIndex }) => {
                  const c_index = originalIndex;

                  return (
                    <React.Fragment key={category.assetId || c_index}>
                      <TableRow>
                        <TableCell
                          style={{
                            position: "sticky",
                            left: 0,
                            background: "white",
                            zIndex: 2,
                            boxShadow: "2px 0 5px rgba(0, 0, 0, 0.05)",
                            fontWeight: "bold",
                          }}
                        >
                          {category.assetCategory}{" "}
                          {canEditSchedule && (
                            <IconButtonWithTooltip
                              icon={AddCircleOutlineIcon}
                              tooltipTitle='Add Row'
                              onClick={() => addNewRow(c_index)}
                              color='primary'
                              size='small'
                            />
                          )}
                        </TableCell>
                        <TableCell
                          colSpan={showNetbookValue ? 13 : 11}
                        ></TableCell>
                        {canEditSchedule && (
                          <TableCell
                            align='center'
                            style={{
                              position: "sticky",
                              right: 0,
                              background: "white",
                              boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
                              minWidth: 100,
                            }}
                          >
                            <IconButtonWithTooltip
                              icon={AddCircleOutlineIcon}
                              tooltipTitle='Add Row'
                              onClick={() => addNewRow(c_index)}
                              color='primary'
                              size='small'
                            />
                          </TableCell>
                        )}
                      </TableRow>
                      {category.disposalDetails.map((row, i) => {
                        const isSourceDetail = isFaRegisterSourceDetail(row);

                        return (
                          <TableRow key={i}>
                            <TableCell
                              style={{
                                position: "sticky",
                                left: 0,
                                background: "white",
                                zIndex: 2,
                                boxShadow: "2px 0 5px rgba(0, 0, 0, 0.05)",
                              }}
                              align={columnSettings[0].align}
                            >
                              <TextFieldForName
                                value={row.description}
                                onChange={handleInputChange}
                                reference={{
                                  index: i,
                                  c_index: c_index,
                                  field: columnSettings[0].field,
                                  field_type: columnSettings[0].type,
                                }}
                                disabled={row.isHirePurchase || isSourceDetail}
                                canEdit={canEditSchedule}
                              />
                            </TableCell>
                            <TableCell align={columnSettings[1].align}>
                              <GroupedSelect
                                value={row.CaName || ""}
                                onChange={(e) =>
                                  handleInputChange(e.target.value, {
                                    index: i,
                                    c_index: c_index,
                                    field: columnSettings[1].field,
                                    field_type: columnSettings[1].type,
                                  })
                                }
                                options={caTypeOptions}
                                enableSearch
                                disabled={row.isHirePurchase || isSourceDetail}
                                canEdit={canEditSchedule}
                              />
                            </TableCell>
                            <TableCell align={columnSettings[14].align}>
                              <TextFieldForViewOnly
                                value={
                                  ["S16 - LIA", "S16 - IBA"].includes(
                                    row.CaName
                                  )
                                    ? row.CaName
                                    : row.CaSummary
                                }
                              />
                            </TableCell>
                            <TableCell align={columnSettings[2].align}>
                              <TextFieldForViewOnly
                                value={row.isHirePurchase ? "Yes" : "No"}
                              />
                            </TableCell>
                            <TableCell align={columnSettings[3].align}>
                              <YearPicker
                                year={row.yaOfPurchase}
                                onChange={handleInputChange}
                                customMaxDate={
                                  new Date(
                                    new Date().setFullYear(
                                      company.yearOfAssessment
                                    )
                                  )
                                }
                                reference={{
                                  index: i,
                                  c_index: c_index,
                                  field: columnSettings[3].field,
                                  field_type: columnSettings[3].type,
                                }}
                                disabled={
                                  isCaNameDisabled(row) ||
                                  row.isHirePurchase ||
                                  isSourceDetail
                                }
                                canEdit={canEditSchedule}
                              />
                            </TableCell>
                            <TableCell align={columnSettings[4].align}>
                              {canEditSchedule ? (
                                <Checkbox
                                  checked={row.isFullyClaimed}
                                  onChange={(e) =>
                                    handleInputChange(e.target.checked, {
                                      index: i,
                                      c_index: c_index,
                                      field: columnSettings[4].field,
                                      field_type: columnSettings[4].type,
                                    })
                                  }
                                  disabled={isCaNameDisabled(row)}
                                />
                              ) : (
                                <TextFieldForViewOnly
                                  value={row.isFullyClaimed ? "Yes" : "No"}
                                />
                              )}
                            </TableCell>
                            <TableCell align={columnSettings[5].align}>
                              {row.isFullyClaimed ? (
                                "Fully Claimed"
                              ) : (
                                <LimitedTextField
                                  value={row.noOfYearsBf}
                                  onChange={handleInputChange}
                                  limitationType={LIMITATION_TYPES.ONLY_NUMBERS}
                                  maxValue={
                                    row.CaName &&
                                    ![
                                      "Revenue",
                                      "No Claim",
                                      "Renovation",
                                    ].includes(row.CaName)
                                      ? getCaSettingByKey(
                                          caSettings,
                                          row.CaName
                                        )?.numberOfYears
                                      : null
                                  }
                                  warningMessage={
                                    row.CaName &&
                                    ![
                                      "Revenue",
                                      "No Claim",
                                      "Renovation",
                                    ].includes(row.CaName)
                                      ? `Value is limited to ${
                                          getCaSettingByKey(
                                            caSettings,
                                            row.CaName
                                          )?.numberOfYears
                                        } years`
                                      : ""
                                  }
                                  reference={{
                                    index: i,
                                    c_index: c_index,
                                    field: columnSettings[5].field,
                                    field_type: columnSettings[5].type,
                                  }}
                                  disabled={
                                    isCaNameDisabled(row) ||
                                    row.isHirePurchase ||
                                    isSourceDetail
                                  }
                                  canEdit={canEditSchedule}
                                />
                              )}
                            </TableCell>
                            <TableCell align={columnSettings[6].align}>
                              <TextFieldForAmount
                                value={row.costInDetails}
                                onChange={handleInputChange}
                                reference={{
                                  index: i,
                                  c_index: c_index,
                                  field: columnSettings[6].field,
                                  field_type: columnSettings[6].type,
                                }}
                                disabled={
                                  !row.CaName ||
                                  row.CaName === "" ||
                                  row.isHirePurchase ||
                                  isSourceDetail
                                }
                                canEdit={canEditSchedule}
                              />
                            </TableCell>
                            <TableCell align={columnSettings[7].align}>
                              <TextFieldForViewOnly
                                value={
                                  row.isHirePurchase
                                    ? formatAmount(row.hpPrincipalPaid)
                                    : "-"
                                }
                              />
                            </TableCell>
                            <TableCell align={columnSettings[8].align}>
                              {canEditSchedule &&
                              row.isHirePurchase &&
                              row.asset ? (
                                <Stack
                                  direction='row'
                                  alignItems='center'
                                  spacing={1}
                                >
                                  <TextFieldForAmount
                                    value={row.twdvBf}
                                    onChange={handleInputChange}
                                    reference={{
                                      index: i,
                                      c_index: c_index,
                                      field: columnSettings[8].field,
                                      field_type: columnSettings[8].type,
                                    }}
                                    disabled={
                                      isCaNameDisabled(row) || isSourceDetail
                                    }
                                    canEdit={canEditSchedule}
                                  />
                                  <StyledIconButton
                                    icon={RefreshIcon}
                                    onClick={() => {
                                      const recalculated =
                                        getUnallocatedPrincipal(
                                          row.asset,
                                          company.yearOfAssessment
                                        );

                                      handleInputChange(recalculated, {
                                        index: i,
                                        c_index: c_index,
                                        field: columnSettings[8].field,
                                        field_type: columnSettings[8].type,
                                      });
                                    }}
                                    tooltip='Recalculate TWDV B/F'
                                    iconColor='primary.main'
                                    disabled={isSourceDetail}
                                  />
                                </Stack>
                              ) : (
                                <Box display='flex' justifyContent='flex-end'>
                                  <TextFieldForAmount
                                    value={row.twdvBf}
                                    onChange={handleInputChange}
                                    reference={{
                                      index: i,
                                      c_index: c_index,
                                      field: columnSettings[8].field,
                                      field_type: columnSettings[8].type,
                                    }}
                                    disabled={
                                      isCaNameDisabled(row) || isSourceDetail
                                    }
                                    canEdit={canEditSchedule}
                                  />
                                </Box>
                              )}
                            </TableCell>
                            <TableCell align={columnSettings[9].align}>
                              <TextFieldForAmount
                                value={row.salesProceeds}
                                onChange={handleInputChange}
                                reference={{
                                  index: i,
                                  c_index: c_index,
                                  field: columnSettings[9].field,
                                  field_type: columnSettings[9].type,
                                }}
                                disabled={isCaNameDisabled(row)}
                                canEdit={canEditSchedule}
                              />
                            </TableCell>
                            <TableCell align={columnSettings[10].align}>
                              <TextFieldForViewOnly
                                value={
                                  isNoBaBcCaName(row.CaName)
                                    ? "N.A."
                                    : formatAmount(row.bcBa)
                                }
                              />
                            </TableCell>
                            {showNetbookValue && (
                              <TableCell align={columnSettings[11].align}>
                                <TextFieldForAmount
                                  value={row.netBookValue}
                                  onChange={handleInputChange}
                                  reference={{
                                    index: i,
                                    c_index: c_index,
                                    field: columnSettings[11].field,
                                    field_type: columnSettings[11].type,
                                  }}
                                  disabled={!row.CaName || row.CaName === ""}
                                  canEdit={canEditSchedule}
                                />
                              </TableCell>
                            )}
                            {showNetbookValue && (
                              <TableCell align={columnSettings[12].align}>
                                <TextFieldForViewOnly
                                  value={formatAmount(row.disposalGainLoss)}
                                />
                              </TableCell>
                            )}
                            {canEditSchedule && (
                              <TableCell
                                style={{
                                  position: "sticky",
                                  right: 0,
                                  background: "white",
                                  boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
                                  minWidth: 100,
                                }}
                                align={columnSettings[13].align}
                              >
                                {!row.isHirePurchase && !isSourceDetail && (
                                  <IconButtonWithTooltip
                                    icon={DeleteOutlineIcon}
                                    tooltipTitle='Remove Row'
                                    onClick={() => removeRow(c_index, i)}
                                    color='error'
                                    size='small'
                                  />
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                      {totalAssetLines &&
                        (() => {
                          const targetAsset = totalAssetById.get(
                            category.assetId
                          );

                          if (targetAsset) {
                            return (
                              <TableRow key={targetAsset.assetId}>
                                <TableCell
                                  style={{
                                    position: "sticky",
                                    left: 0,
                                    background: "white",
                                    zIndex: 2,
                                    boxShadow: "2px 0 5px rgba(0, 0, 0, 0.05)",
                                    fontWeight: "bold",
                                    textAlign: "right",
                                  }}
                                >
                                  Sub Total
                                </TableCell>
                                <TableCell colSpan={6}></TableCell>
                                <TableCell align={columnSettings[6].align}>
                                  <BalanceVerification
                                    grossBalance={
                                      targetAsset.totalDetails.costInDetails ||
                                      0
                                    }
                                    expectedTotal={Math.abs(
                                      category.cost.disposal
                                    )}
                                  />
                                </TableCell>
                                <TableCell align={columnSettings[7].align}>
                                  <TextFieldForViewOnly
                                    value={formatAmount(
                                      targetAsset.totalDetails.hpPrincipalPaid
                                    )}
                                  />
                                </TableCell>
                                <TableCell align={columnSettings[8].align}>
                                  <TextFieldForViewOnly
                                    value={formatAmount(
                                      targetAsset.totalDetails.twdvBf
                                    )}
                                  />
                                </TableCell>
                                <TableCell align={columnSettings[9].align}>
                                  <TextFieldForViewOnly
                                    value={formatAmount(
                                      targetAsset.totalDetails.salesProceeds
                                    )}
                                  />
                                </TableCell>
                                <TableCell align={columnSettings[10].align}>
                                  <TextFieldForViewOnly
                                    value={formatAmount(
                                      targetAsset.totalDetails.bcBa
                                    )}
                                  />
                                </TableCell>
                                {showNetbookValue && (
                                  <TableCell align={columnSettings[11].align}>
                                    <TextFieldForViewOnly
                                      value={formatAmount(
                                        targetAsset.totalDetails.netBookValue
                                      )}
                                    />
                                  </TableCell>
                                )}
                                {showNetbookValue && (
                                  <TableCell align={columnSettings[12].align}>
                                    <TextFieldForViewOnly
                                      value={formatAmount(
                                        targetAsset.totalDetails
                                          .disposalGainLoss
                                      )}
                                    />
                                  </TableCell>
                                )}
                                {canEditSchedule && (
                                  <TableCell
                                    style={{
                                      position: "sticky",
                                      right: 0,
                                      background: "white",
                                      boxShadow:
                                        "-2px 0 5px rgba(0, 0, 0, 0.05)",
                                      minWidth: 100,
                                    }}
                                  ></TableCell>
                                )}
                              </TableRow>
                            );
                          }
                          return null;
                        })()}
                    </React.Fragment>
                  );
                })}

                {hasRegisteredAssetSources && (
                  <>
                    {registeredSourceCategories.map(
                      renderRegisteredSourceCategory
                    )}
                  </>
                )}

                {totalAssetLines.overallTotalDetails && (
                  <TableRow
                    sx={(theme) => ({
                      position: "sticky",
                      bottom: 0,
                      bgcolor: "background.paper",
                      zIndex: 4,
                      boxShadow:
                        `0 -2px 0 ${theme.palette.primary.main}, ` +
                        `0 -8px 16px ${alpha(theme.palette.common.black, 0.06)}`,
                    })}
                  >
                    <TableCell
                      sx={(theme) => ({
                        position: "sticky",
                        left: 0,
                        bgcolor: "background.paper",
                        zIndex: 5,
                        boxShadow:
                          `inset -1px 0 0 ${theme.palette.divider}, ` +
                          `6px 0 12px ${alpha(theme.palette.common.black, 0.08)}`,
                        borderTop: "1px solid",
                        borderTopColor: "primary.main",
                        borderRight: "1px solid",
                        borderRightColor: "divider",
                        borderBottom: "1px solid",
                        borderBottomColor: "divider",
                        color: "primary.main",
                        fontWeight: 800,
                        textAlign: "right",
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                      })}
                    >
                      Total
                    </TableCell>
                    <TableCell
                      colSpan={6}
                      sx={{
                        bgcolor: "background.paper",
                        borderTop: "1px solid",
                        borderTopColor: "primary.main",
                        borderBottom: "1px solid",
                        borderBottomColor: "divider",
                      }}
                    ></TableCell>
                    <TableCell
                      align={columnSettings[6].align}
                      sx={{
                        bgcolor: "background.paper",
                        borderTop: "1px solid",
                        borderTopColor: "primary.main",
                        borderBottom: "1px solid",
                        borderBottomColor: "divider",
                      }}
                    >
                      <TextFieldForViewOnly
                        value={formatAmount(
                          totalAssetLines.overallTotalDetails.costInDetails
                        )}
                        sx={{ fontWeight: 700, color: "primary.main" }}
                      />
                    </TableCell>
                    <TableCell
                      align={columnSettings[7].align}
                      sx={{
                        bgcolor: "background.paper",
                        borderTop: "1px solid",
                        borderTopColor: "primary.main",
                        borderBottom: "1px solid",
                        borderBottomColor: "divider",
                      }}
                    >
                      <TextFieldForViewOnly
                        value={formatAmount(
                          totalAssetLines.overallTotalDetails.hpPrincipalPaid
                        )}
                        sx={{ fontWeight: 700, color: "primary.main" }}
                      />
                    </TableCell>
                    <TableCell
                      align={columnSettings[8].align}
                      sx={{
                        bgcolor: "background.paper",
                        borderTop: "1px solid",
                        borderTopColor: "primary.main",
                        borderBottom: "1px solid",
                        borderBottomColor: "divider",
                      }}
                    >
                      <TextFieldForViewOnly
                        value={formatAmount(
                          totalAssetLines.overallTotalDetails.twdvBf
                        )}
                        sx={{ fontWeight: 700, color: "primary.main" }}
                      />
                    </TableCell>
                    <TableCell
                      align={columnSettings[9].align}
                      sx={{
                        bgcolor: "background.paper",
                        borderTop: "1px solid",
                        borderTopColor: "primary.main",
                        borderBottom: "1px solid",
                        borderBottomColor: "divider",
                      }}
                    >
                      <TextFieldForViewOnly
                        value={formatAmount(
                          totalAssetLines.overallTotalDetails.salesProceeds
                        )}
                        sx={{ fontWeight: 700, color: "primary.main" }}
                      />
                    </TableCell>
                    <TableCell
                      align={columnSettings[10].align}
                      sx={{
                        bgcolor: "background.paper",
                        borderTop: "1px solid",
                        borderTopColor: "primary.main",
                        borderBottom: "1px solid",
                        borderBottomColor: "divider",
                      }}
                    >
                      <TextFieldForViewOnly
                        value={formatAmount(
                          totalAssetLines.overallTotalDetails.bcBa
                        )}
                        sx={{ fontWeight: 700, color: "primary.main" }}
                      />
                    </TableCell>
                    {showNetbookValue && (
                      <TableCell
                        align={columnSettings[11].align}
                        sx={{
                          bgcolor: "background.paper",
                          borderTop: "1px solid",
                          borderTopColor: "primary.main",
                          borderBottom: "1px solid",
                          borderBottomColor: "divider",
                        }}
                      >
                        <TextFieldForViewOnly
                          value={formatAmount(
                            totalAssetLines.overallTotalDetails.netBookValue
                          )}
                          sx={{ fontWeight: 700, color: "primary.main" }}
                        />
                      </TableCell>
                    )}
                    {showNetbookValue && (
                      <TableCell
                        align={columnSettings[12].align}
                        sx={{
                          bgcolor: "background.paper",
                          borderTop: "1px solid",
                          borderTopColor: "primary.main",
                          borderBottom: "1px solid",
                          borderBottomColor: "divider",
                        }}
                      >
                        <TextFieldForViewOnly
                          value={formatAmount(
                            totalAssetLines.overallTotalDetails.disposalGainLoss
                          )}
                          sx={{ fontWeight: 700, color: "primary.main" }}
                        />
                      </TableCell>
                    )}
                    {canEditSchedule && (
                      <TableCell
                        sx={(theme) => ({
                          position: "sticky",
                          right: 0,
                          bgcolor: "background.paper",
                          boxShadow:
                            `inset 1px 0 0 ${theme.palette.divider}, ` +
                            `-6px 0 12px ${alpha(theme.palette.common.black, 0.08)}`,
                          borderTop: "1px solid",
                          borderTopColor: "primary.main",
                          borderLeft: "1px solid",
                          borderLeftColor: "divider",
                          borderBottom: "1px solid",
                          borderBottomColor: "divider",
                          minWidth: 100,
                        })}
                      ></TableCell>
                    )}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </div>
  );

  return (
    <Box sx={{ padding: 4, position: "relative" }} aria-busy={loading}>
      {loading && (
        <Box
          role='status'
          aria-live='polite'
          aria-label='Preparing fixed asset disposal schedule'
          sx={{
            position: "absolute",
            inset: "0 0 auto 0",
            height: "70vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            px: 3,
            textAlign: "center",
            pointerEvents: "none",

            "@keyframes logoFade": {
              "0%, 100%": { opacity: 0.8 },
              "50%": { opacity: 1 },
            },

            "@keyframes progressMove": {
              "0%": { transform: "translateX(-100%)" },
              "100%": { transform: "translateX(300%)" },
            },

            "@media (prefers-reduced-motion: reduce)": {
              "& .loading-logo, & .loading-progress": {
                animation: "none",
              },
              "& .loading-progress": {
                transform: "translateX(0)",
              },
            },
          }}
        >
          {/* Brand identity */}
          <Box
            component='img'
            className='loading-logo'
            src={loadingLogo}
            alt=''
            sx={{
              width: { xs: 80, sm: 88 },
              height: { xs: 80, sm: 88 },
              objectFit: "contain",
              mb: 3,
              opacity: 0.9,
              animation: "logoFade 3s ease-in-out infinite",
            }}
          />

          {/* Primary status */}
          <Typography
            sx={{
              fontSize: { xs: "1rem", sm: "1.1rem" },
              fontWeight: 600,
              color: "text.primary",
              letterSpacing: "-0.01em",
              lineHeight: 1.5,
            }}
          >
            Preparing Disposal Schedule
          </Typography>

          {/* Supporting description */}
          <Typography
            color='text.secondary'
            sx={{
              mt: 1,
              fontSize: "0.8rem",
              fontWeight: 400,
              lineHeight: 1.6,
              maxWidth: 320,
            }}
          >
            Processing fixed asset information. Please wait a moment.
          </Typography>

          {/* Minimal progress indicator */}
          <Box
            sx={{
              mt: 3.5,
              width: 180,
              height: 3,
              bgcolor: "action.hover",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Box
              className='loading-progress'
              sx={{
                width: "25%",
                height: "100%",
                bgcolor: "primary.main",
                borderRadius: 2,
                animation: "progressMove 2s ease-in-out infinite",
              }}
            />
          </Box>

          {/* System status */}
          <Typography
            sx={{
              mt: 2,
              fontSize: "0.7rem",
              fontWeight: 500,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "text.disabled",
            }}
          >
            Processing
          </Typography>
        </Box>
      )}
      <Box sx={{ visibility: loading ? "hidden" : "visible" }}>
        <ScheduleHeader
          {...{
            title: schedule.type,
            companyName: company.name,
            uen: company.uen,
            yearOfAssessment: company.yearOfAssessment,
            financialYearStart: company.financialYearStart,
            financialYearEnd: company.financialYearEnd,
            deleteSchedule,
            handleSave,
            handleExcel: handleOpenExcelUploadDialog,
            canEditSchedule,
          }}
        >
          <PdfGenerator
            schedule={schedule}
            company={{
              name: company.name,
              uen: company.uen,
              yearOfAssessment: company.yearOfAssessment,
              financialYearStart: company.financialYearStart,
              financialYearEnd: company.financialYearEnd,
              currency: company.currency,
              currencyRate: company.currencyRate,
            }}
            assetLines={getCombinedAssetLines()}
            totalAssetLines={totalAssetLines}
            showNetbookValue={showNetbookValue}
            twdvSummary={twdvSummary}
            bcSummary={bcSummary}
            refSources={refSources}
            notes={notes}
            reviewNotes={reviewNotes}
          />
        </ScheduleHeader>
        <br />

        {!fullscreenOpen && renderMainContent()}

        <br />

        <Stack spacing={2}>
          <DisposalBaBcSummary bcSummary={bcSummary} />
          <DisposalTwdvSummary company={company} twdvSummary={twdvSummary} />
        </Stack>

        <Divider sx={{ marginY: 4 }} />
        <ScheduleNotes
          notes={notes}
          setNotes={setNotes}
          reviewNotes={reviewNotes}
          setReviewNotes={setReviewNotes}
          enableEdit={canEditSchedule}
        />

        <DisposalExcelUpload
          assetLines={assetLines}
          caSettings={caSettings}
          caTypeOptions={caTypeUploadOptions}
          yearOfAssessment={company.yearOfAssessment}
          onExcelAddRows={onExcelAddRows}
          open={excelDialogOpen}
          handleCloseDialog={handleCloseExcelUploadDialog}
        />

        <FullscreenDialog
          open={fullscreenOpen}
          onClose={() => setFullscreenOpen(false)}
          title={schedule.type}
          subtitle={`${company.name} (${company.uen}) - YA ${company.yearOfAssessment}`}
        >
          {renderMainContent}
        </FullscreenDialog>

        <ValidationWarningDialog
          open={validationWarning.open}
          title='Save with validation warning?'
          subtitle='Fixed asset disposal checks did not match.'
          warnings={validationWarning.warnings}
          loading={validationWarning.loading}
          onClose={() =>
            setValidationWarning({
              open: false,
              warnings: [],
              pendingAssetLines: null,
              loading: false,
            })
          }
          onConfirm={handleConfirmValidationSave}
        />
      </Box>
    </Box>
  );
};

export default FaDisposalSchedule;
